import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GraduationCap,
  Check,
  Play,
  Clock,
  ChevronRight,
  Trophy,
  RotateCcw,
  ListChecks,
  ArrowRight,
} from 'lucide-react';
import {
  useProCourse,
  useSetLessonProgress,
  useCompleteAllLessons,
  useResetCourseProgress,
} from '@/hooks/use-pro-formation';
import {
  computeCourseProgress,
  flattenLessons,
  nextLessonAfter,
  formatDuration,
} from '@erp/domain/formations';
import { ProgressRing } from '@/components/formation/ProgressRing';
import { LessonPlayer } from '@/components/formation/LessonPlayer';
import type { ProFormationChapter, ProFormationLesson } from '@erp/data-access/interfaces';

type Lang = 'fr' | 'ar' | 'en';

function useLang(): Lang {
  const { i18n } = useTranslation();
  return i18n.language.startsWith('ar') ? 'ar' : i18n.language.startsWith('en') ? 'en' : 'fr';
}

/** Le premier libellé renseigné, en partant de la langue courante. */
function pick(
  value: { fr: string | null; ar: string | null; en: string | null },
  lang: Lang,
): string | null {
  const ordered =
    lang === 'ar'
      ? [value.ar, value.fr, value.en]
      : lang === 'en'
        ? [value.en, value.fr, value.ar]
        : [value.fr, value.en, value.ar];
  return ordered.find((v) => v && v.trim()) ?? null;
}

function lessonTitle(lesson: ProFormationLesson, lang: Lang): string | null {
  return pick({ fr: lesson.titleFr, ar: lesson.titleAr, en: lesson.titleEn }, lang);
}
function lessonDescription(lesson: ProFormationLesson, lang: Lang): string | null {
  return pick(
    { fr: lesson.descriptionFr, ar: lesson.descriptionAr, en: lesson.descriptionEn },
    lang,
  );
}
function chapterTitle(chapter: ProFormationChapter, lang: Lang): string | null {
  return pick({ fr: chapter.titleFr, ar: chapter.titleAr, en: chapter.titleEn }, lang);
}

// ============================================================

export function FormationPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const courseQuery = useProCourse();
  const setProgress = useSetLessonProgress();
  const completeAll = useCompleteAllLessons();
  const resetProgress = useResetCourseProgress();

  const chapters = useMemo(() => courseQuery.data ?? [], [courseQuery.data]);
  const flat = useMemo(() => flattenLessons(chapters), [chapters]);
  const progress = useMemo(() => computeCourseProgress(chapters), [chapters]);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const deepLinkConsumed = useRef(false);

  // La leçon ouverte : `?lecon=` (posé par la bannière d'accueil) sinon la
  // première non terminée, sinon la toute première.
  const requestedId = searchParams.get('lecon');
  useEffect(() => {
    if (!flat.length) return;

    // Le lien profond ne vaut qu'UNE fois, et on le retire de l'URL dans la
    // foulée. Sinon, cocher la leçon ciblée fait repasser le parcours (donc
    // cet effet), qui la ré-ouvrirait aussitôt : l'électricien serait renvoyé
    // en boucle sur la vidéo qu'il vient de terminer.
    if (!deepLinkConsumed.current && requestedId && flat.some((l) => l.id === requestedId)) {
      deepLinkConsumed.current = true;
      setActiveId(requestedId);
      const next = new URLSearchParams(searchParams);
      next.delete('lecon');
      setSearchParams(next, { replace: true });
      return;
    }

    // Sinon : on garde la leçon en cours, ou on ouvre la première non terminée.
    setActiveId((current) => current ?? progress.nextLessonId ?? flat[0]!.id);
  }, [flat, requestedId, progress.nextLessonId, searchParams, setSearchParams]);

  const active = useMemo(
    () => flat.find((l) => l.id === activeId) ?? null,
    [flat, activeId],
  );

  const openLesson = useCallback((id: string) => setActiveId(id), []);

  const markAndAdvance = useCallback(
    (lesson: ProFormationLesson) => {
      if (!lesson.completedAt) {
        setProgress.mutate({ lessonId: lesson.id, completed: true });
      }
      const next = nextLessonAfter(chapters, lesson.id);
      if (next) {
        setActiveId(next.id);
      } else {
        // C'était la dernière : on fête, l'électricien a fini.
        setShowCelebration(true);
      }
    },
    [chapters, setProgress],
  );

  const onCompleteAll = () => {
    if (!window.confirm(t('pro.formation.skip_confirm', 'Marquer toutes les vidéos comme vues ?')))
      return;
    completeAll.mutate(undefined, { onSuccess: () => setShowCelebration(true) });
  };

  const onRestart = () => {
    resetProgress.mutate(undefined, {
      onSuccess: () => {
        setShowCelebration(false);
        if (flat[0]) setActiveId(flat[0].id);
      },
    });
  };

  // ---- États hors parcours -------------------------------------------

  if (courseQuery.isLoading) {
    return <FormationSkeleton />;
  }

  if (!flat.length) {
    return (
      <div className="pro-card p-10 text-center pro-fade-in">
        <div
          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{ background: 'var(--pro-primary-subtle)' }}
        >
          <GraduationCap className="h-7 w-7" style={{ color: 'var(--pro-primary)' }} />
        </div>
        <h2
          className="text-[18px] font-bold"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {t('pro.formation.empty_title', 'Aucune formation pour le moment')}
        </h2>
        <p className="mt-1 text-[14px]" style={{ color: 'var(--pro-text-grey)' }}>
          {t(
            'pro.formation.empty_body',
            'Votre fournisseur n’a pas encore publié de vidéos de prise en main. Revenez bientôt.',
          )}
        </p>
      </div>
    );
  }

  const activeTitle = active
    ? (lessonTitle(active, lang) ?? t('pro.formation.untitled_lesson', 'Leçon'))
    : '';
  const activeIndex = active ? flat.findIndex((l) => l.id === active.id) : -1;
  const isLast = activeIndex >= 0 && activeIndex === flat.length - 1;

  return (
    <div className="space-y-4">
      <CourseHero
        percent={progress.percent}
        completed={progress.completedLessons}
        total={progress.totalLessons}
        totalSeconds={flat.reduce((acc, l) => acc + (l.durationSeconds ?? 0), 0)}
        isComplete={progress.isComplete}
        isBusy={completeAll.isPending || resetProgress.isPending}
        onCompleteAll={onCompleteAll}
        onRestart={onRestart}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        {/* ---- Lecteur ---- */}
        <div className="min-w-0 space-y-3">
          {showCelebration && progress.isComplete ? (
            <CelebrationCard
              onRestart={onRestart}
              onGoToCatalogue={() => navigate('/catalogue')}
              onClose={() => setShowCelebration(false)}
            />
          ) : (
            active && (
              <div key={active.id} className="pro-fade-in space-y-3">
                <LessonPlayer
                  lesson={active}
                  title={activeTitle}
                  onEnded={() => markAndAdvance(active)}
                />

                <div className="pro-card p-4 md:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="text-[11px] font-medium uppercase tracking-wide"
                        style={{ color: 'var(--pro-primary-text)' }}
                      >
                        {t('pro.formation.lesson_of', 'Vidéo {{index}} sur {{total}}', {
                          index: activeIndex + 1,
                          total: flat.length,
                        })}
                      </div>
                      <h2
                        className="mt-0.5 text-[17px] font-bold leading-snug sm:text-[19px]"
                        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
                      >
                        {activeTitle}
                      </h2>
                    </div>
                    {active.durationSeconds ? (
                      <span
                        className="flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-medium tabular-nums"
                        style={{
                          background: 'var(--pro-grey-subtle)',
                          color: 'var(--pro-text-grey)',
                        }}
                      >
                        <Clock className="h-3.5 w-3.5" />
                        {formatDuration(active.durationSeconds)}
                      </span>
                    ) : null}
                  </div>

                  {lessonDescription(active, lang) && (
                    <p
                      className="mt-2 whitespace-pre-line text-[14px] leading-relaxed"
                      style={{ color: 'var(--pro-text-muted)' }}
                    >
                      {lessonDescription(active, lang)}
                    </p>
                  )}

                  {/* Empilés sous 640 px : côte à côte, « Vidéo suivante » et
                      « Retirer la coche » tombaient sous 44 px de haut utile
                      une fois compressés. */}
                  <div className="mt-4 flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <button
                      type="button"
                      onClick={() => markAndAdvance(active)}
                      className="flex h-11 items-center justify-center gap-2 rounded-xl px-5 text-[14px] font-semibold text-white"
                      style={{
                        background: active.completedAt
                          ? 'var(--pro-success)'
                          : 'var(--pro-primary)',
                      }}
                    >
                      {active.completedAt ? (
                        <>
                          <Check className="h-4 w-4 shrink-0" />
                          {isLast
                            ? t('pro.formation.finish', 'Terminer le parcours')
                            : t('pro.formation.next', 'Vidéo suivante')}
                          {!isLast && (
                            <ChevronRight className="h-4 w-4 shrink-0 rtl:-scale-x-100" />
                          )}
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 shrink-0" />
                          {t('pro.formation.mark_done', 'J’ai vu cette vidéo')}
                        </>
                      )}
                    </button>

                    {active.completedAt && (
                      <button
                        type="button"
                        onClick={() =>
                          setProgress.mutate({ lessonId: active.id, completed: false })
                        }
                        className="flex h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-[13px] font-medium"
                        style={{
                          border: '1px solid var(--pro-border)',
                          color: 'var(--pro-text-grey)',
                        }}
                      >
                        <RotateCcw className="h-3.5 w-3.5 shrink-0" />
                        {t('pro.formation.unmark', 'Retirer la coche')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>

        {/* ---- Playlist ---- */}
        <Playlist
          chapters={chapters}
          lang={lang}
          activeId={activeId}
          onSelect={openLesson}
        />
      </div>
    </div>
  );
}

// ============================================================
// En-tête : anneau + barre de progression + actions
// ============================================================

function CourseHero({
  percent, completed, total, totalSeconds, isComplete, isBusy, onCompleteAll, onRestart,
}: {
  percent: number;
  completed: number;
  total: number;
  totalSeconds: number;
  isComplete: boolean;
  isBusy: boolean;
  onCompleteAll: () => void;
  onRestart: () => void;
}) {
  const { t } = useTranslation();
  const minutes = Math.round(totalSeconds / 60);

  return (
    <div
      className="pro-card relative overflow-hidden p-4 sm:p-5 md:p-6"
      style={{
        background: 'linear-gradient(135deg, #FF6B1E 0%, #F0490C 55%, #D93A00 100%)',
      }}
    >
      {/* Halos décoratifs — purement visuels. */}
      <div
        className="pointer-events-none absolute -end-16 -top-24 h-56 w-56 rounded-full"
        style={{ background: 'rgba(255,255,255,0.13)' }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -end-4 top-20 h-32 w-32 rounded-full"
        style={{ background: 'rgba(255,255,255,0.09)' }}
        aria-hidden
      />

      {/* Sous 640 px l'anneau et le texte restent sur une ligne (anneau plus
          petit, gouttière serrée) : les envoyer l'un sous l'autre laissait un
          rond seul en haut de la carte. Le bouton, lui, passe pleine largeur. */}
      <div className="relative flex flex-wrap items-center gap-x-4 gap-y-3 sm:gap-5">
        <ProgressRing percent={percent} size={62} className="sm:hidden">
          <span
            className="text-[14px] font-bold tabular-nums text-white"
            style={{ fontFamily: 'Figtree' }}
          >
            {percent}%
          </span>
        </ProgressRing>
        <ProgressRing percent={percent} className="hidden sm:block">
          <span
            className="text-[17px] font-bold tabular-nums text-white"
            style={{ fontFamily: 'Figtree' }}
          >
            {percent}%
          </span>
        </ProgressRing>

        <div className="min-w-0 flex-1 basis-40">
          <div className="flex items-center gap-1.5 text-[12px] font-medium text-white/85">
            <GraduationCap className="h-4 w-4 shrink-0" />
            <span className="truncate">{t('pro.formation.badge', 'Prise en main')}</span>
          </div>
          <h1
            className="mt-0.5 text-[19px] font-bold leading-tight text-white sm:text-[22px] md:text-[26px]"
            style={{ fontFamily: 'Figtree' }}
          >
            {isComplete
              ? t('pro.formation.hero_done', 'Vous maîtrisez la plateforme')
              : t('pro.formation.hero_title', 'Apprenez à utiliser votre espace')}
          </h1>
          <p className="mt-1 text-[12px] text-white/85 sm:text-[13px]">
            {t('pro.formation.hero_progress', '{{completed}} vidéo(s) sur {{total}}', {
              completed,
              total,
            })}
            {minutes > 0 &&
              ` · ${t('pro.formation.hero_duration', 'environ {{minutes}} min', { minutes })}`}
          </p>

          <div
            className="mt-3 h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: 'rgba(255,255,255,0.28)' }}
          >
            <div
              className="pro-progress-fill h-full rounded-full bg-white"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <div className="w-full sm:w-auto">
          {isComplete ? (
            <button
              type="button"
              onClick={onRestart}
              disabled={isBusy}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-white/20 px-4 text-[13px] font-semibold text-white ring-1 ring-white/50 backdrop-blur hover:bg-white/30 disabled:opacity-60 sm:w-auto"
            >
              <RotateCcw className="h-4 w-4 shrink-0" />
              {t('pro.formation.restart', 'Recommencer')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onCompleteAll}
              disabled={isBusy}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-white px-4 text-[13px] font-semibold disabled:opacity-60 sm:w-auto"
              style={{ color: 'var(--pro-primary-text)' }}
            >
              <ListChecks className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {t('pro.formation.skip', 'Je connais déjà, tout marquer')}
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Playlist — chapitres et leçons
// ============================================================

function Playlist({
  chapters, lang, activeId, onSelect,
}: {
  chapters: ProFormationChapter[];
  lang: Lang;
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  let runningIndex = 0;

  return (
    <aside className="pro-card h-fit overflow-hidden lg:sticky lg:top-4">
      <div
        className="px-4 py-3"
        style={{ borderBottom: '1px solid var(--pro-line)' }}
      >
        <div
          className="text-[14px] font-semibold"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {t('pro.formation.playlist', 'Le programme')}
        </div>
      </div>

      {/* Sur mobile la playlist se déroule dans le flux : un conteneur qui
          défile dans une page qui défile déjà est une plaie au doigt. */}
      <div className="py-1 lg:max-h-[560px] lg:overflow-y-auto">
        {chapters.map((chapter) => {
          const title = chapterTitle(chapter, lang);
          return (
            <div key={chapter.id}>
              {title && (
                <div
                  className="px-4 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--pro-text-light)' }}
                >
                  {title}
                </div>
              )}
              <ul>
                {chapter.lessons.map((lesson) => {
                  const index = runningIndex++;
                  return (
                    <LessonRow
                      key={lesson.id}
                      lesson={lesson}
                      index={index}
                      lang={lang}
                      isActive={lesson.id === activeId}
                      onSelect={() => onSelect(lesson.id)}
                    />
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function LessonRow({
  lesson, index, lang, isActive, onSelect,
}: {
  lesson: ProFormationLesson;
  index: number;
  lang: Lang;
  isActive: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const done = !!lesson.completedAt;
  const title = lessonTitle(lesson, lang) ?? t('pro.formation.untitled_lesson', 'Leçon');
  const duration = formatDuration(lesson.durationSeconds);

  return (
    <li
      className="pro-rise-in relative"
      data-active={isActive}
      style={{ ['--pro-stagger-i' as string]: index }}
    >
      <span className="pro-lesson-rail" aria-hidden />
      <button
        type="button"
        onClick={onSelect}
        aria-current={isActive ? 'true' : undefined}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-start"
        style={{ background: isActive ? 'var(--pro-primary-subtle)' : 'transparent' }}
      >
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums ${
            isActive && !done ? 'pro-pulse-ring' : ''
          }`}
          style={{
            background: done
              ? 'var(--pro-success)'
              : isActive
                ? 'var(--pro-primary)'
                : 'var(--pro-grey-subtle)',
            color: done || isActive ? 'white' : 'var(--pro-text-grey)',
          }}
        >
          {done ? (
            <Check className="pro-check-in h-3.5 w-3.5" strokeWidth={3} />
          ) : isActive ? (
            <Play className="h-3 w-3 fill-current rtl:-scale-x-100" />
          ) : (
            index + 1
          )}
        </span>

        <span className="min-w-0 flex-1">
          <span
            className="block truncate text-[13px] font-medium"
            style={{
              color: isActive ? 'var(--pro-primary-text)' : 'var(--pro-text-dark)',
              textDecoration: done && !isActive ? 'none' : undefined,
              opacity: done && !isActive ? 0.7 : 1,
            }}
          >
            {title}
          </span>
          {duration && (
            <span
              className="mt-0.5 flex items-center gap-1 text-[11px] tabular-nums"
              style={{ color: 'var(--pro-text-light)' }}
            >
              <Clock className="h-3 w-3" />
              {duration}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

// ============================================================
// Fin de parcours
// ============================================================

const CONFETTI_COLORS = ['#FF6B1E', '#1DAF61', '#F6A723', '#3B82F6', '#FDCEA1', '#DF1C41'];

function CelebrationCard({
  onRestart, onGoToCatalogue, onClose,
}: {
  onRestart: () => void;
  onGoToCatalogue: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="pro-card relative overflow-hidden p-6 text-center sm:p-8 md:p-12">
      {/* Confettis — décoratifs, masqués aux lecteurs d'écran. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-full" aria-hidden>
        {CONFETTI_COLORS.map((color, i) => (
          <span
            key={color}
            className="pro-confetti"
            style={{
              background: color,
              insetInlineStart: `${8 + i * 15}%`,
              ['--pro-confetti-i' as string]: i,
            }}
          />
        ))}
      </div>

      <div
        className="pro-trophy-in relative mx-auto flex h-20 w-20 items-center justify-center rounded-3xl"
        style={{ background: 'var(--pro-primary-subtle)' }}
      >
        <Trophy className="h-10 w-10" style={{ color: 'var(--pro-primary)' }} />
      </div>

      <h2
        className="relative mt-5 text-[20px] font-bold sm:text-[24px]"
        style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
      >
        {t('pro.formation.done_title', 'Bravo, formation terminée !')}
      </h2>
      <p
        className="relative mx-auto mt-2 max-w-md text-[14px] leading-relaxed"
        style={{ color: 'var(--pro-text-grey)' }}
      >
        {t(
          'pro.formation.done_body',
          'Vous avez toutes les clés en main. Passez commande, suivez vos devis et vos commissions en toute autonomie.',
        )}
      </p>

      <div className="relative mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
        <button
          type="button"
          onClick={onGoToCatalogue}
          className="flex h-11 items-center justify-center gap-2 rounded-xl px-5 text-[14px] font-semibold text-white"
          style={{ background: 'var(--pro-primary)' }}
        >
          {t('pro.formation.done_cta', 'Découvrir le catalogue')}
          <ArrowRight className="h-4 w-4 shrink-0 rtl:-scale-x-100" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-[13px] font-medium"
          style={{ border: '1px solid var(--pro-border)', color: 'var(--pro-text-grey)' }}
        >
          {t('pro.formation.done_review', 'Revoir les vidéos')}
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="flex h-11 items-center justify-center gap-1.5 rounded-xl px-4 text-[13px] font-medium"
          style={{ color: 'var(--pro-text-light)' }}
        >
          <RotateCcw className="h-3.5 w-3.5 shrink-0" />
          {t('pro.formation.restart', 'Recommencer')}
        </button>
      </div>
    </div>
  );
}

// ============================================================

function FormationSkeleton() {
  return (
    <div className="space-y-4">
      <div className="pro-card h-[132px] animate-pulse" style={{ background: '#F1EDE9' }} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div
          className="animate-pulse rounded-[18px]"
          style={{ aspectRatio: '16 / 9', background: '#F1EDE9' }}
        />
        <div className="pro-card h-[320px] animate-pulse" style={{ background: '#F1EDE9' }} />
      </div>
    </div>
  );
}
